import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, message, Card, Tag, Popconfirm, Dropdown, Modal, Input, Select, Row, Col, DatePicker, Spin, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import {
  fetchServiceOrders,
  fetchServiceOrder,
  getServiceOrderStatuses,
  fetchCustomerVehicle,
  deleteServiceOrder,
  cancelServiceOrder,
  type ServiceOrderDto,
  type ServiceOrderLineDto,
} from '../services/serviceOrderService';
import { api } from '../services/api';
import {
  buildServiceOrderFicheDocumentHtml,
  buildServiceOrderFicheModalPreviewHtml,
  insertLogoBeforeAdditionalTables,
} from '../utils/serviceOrderFicheDocument';
import type { ServiceOrderAccountPreview } from '../utils/serviceOrderFicheDocument';
import { useTheme } from '../contexts/ThemeContext';
import { useCanDelete } from '../hooks/useCanDelete';
import { useCanCancelServiceOrder } from '../hooks/useCanCancelServiceOrder';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileTextOutlined, PrinterOutlined, MoreOutlined } from '@ant-design/icons';

const MOBILE_BREAKPOINT = 768;
const TABLE_SCROLL_X = 900;

const ServiceOrdersList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const canDelete = useCanDelete();
  const canCancelServiceOrder = useCanCancelServiceOrder();
  const [orders, setOrders] = useState<ServiceOrderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState<{ value: number; displayName: string }[]>([]);
  const [filterCustomerName, setFilterCustomerName] = useState('');
  const [filterPlate, setFilterPlate] = useState('');
  const [filterDateRange, setFilterDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [filterStatus, setFilterStatus] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
  const [cancelModalOrder, setCancelModalOrder] = useState<ServiceOrderDto | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [fichePreviewOpen, setFichePreviewOpen] = useState(false);
  const [fichePreviewHtml, setFichePreviewHtml] = useState<string | null>(null);
  const [fichePreviewLoading, setFichePreviewLoading] = useState(false);
  const [fichePreviewTitle, setFichePreviewTitle] = useState('');
  const [fichePreviewOrderId, setFichePreviewOrderId] = useState<number | null>(null);
  const [fichePrintLoading, setFichePrintLoading] = useState(false);
  const fichePreviewSnapshotRef = useRef<{
    fullOrder: ServiceOrderDto;
    accountData: ServiceOrderAccountPreview;
  } | null>(null);

  useEffect(() => {
    getServiceOrderStatuses().then(setStatuses).catch(() => {});
  }, []);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial list load only; filters applied via "Filtrele"
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!fichePreviewOpen) return;
    const snap = fichePreviewSnapshotRef.current;
    if (!snap) return;
    setFichePreviewHtml(
      buildServiceOrderFicheModalPreviewHtml({
        fullOrder: snap.fullOrder,
        accountData: snap.accountData,
        theme,
      })
    );
  }, [theme, fichePreviewOpen]);

  const loadOrders = async (overrides?: {
    customerName?: string;
    plate?: string;
    orderDateFrom?: string;
    orderDateTo?: string;
    status?: number | null;
  }) => {
    try {
      setLoading(true);
      const effectiveStatus =
        overrides != null && Object.prototype.hasOwnProperty.call(overrides, 'status')
          ? overrides.status
          : filterStatus;
      const data = await fetchServiceOrders({
        customerName: (overrides?.customerName ?? filterCustomerName)?.trim() || undefined,
        plate: (overrides?.plate ?? filterPlate)?.trim() || undefined,
        orderDateFrom: overrides?.orderDateFrom ?? filterDateRange?.[0]?.format('YYYY-MM-DD'),
        orderDateTo: overrides?.orderDateTo ?? filterDateRange?.[1]?.format('YYYY-MM-DD'),
        status: effectiveStatus,
      });
      setOrders(data);
    } catch (error: any) {
      const msg =
        error.response?.data?.message ?? error.message ?? 'Servis fişleri yüklenirken hata oluştu';
      message.error(typeof msg === 'string' ? msg : 'Servis fişleri yüklenirken hata oluştu');
      console.error('fetchServiceOrders error:', error.response?.data ?? error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilterCustomerName('');
    setFilterPlate('');
    setFilterDateRange(null);
    setFilterStatus(null);
    loadOrders({ customerName: '', plate: '', orderDateFrom: undefined, orderDateTo: undefined, status: null });
  };

  const handleAdd = () => {
    navigate('/service-orders/add');
  };

  const handleEdit = (order: ServiceOrderDto) => {
    navigate(`/service-orders/${order.id}`);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteServiceOrder(id);
      message.success('Servis fişi silindi');
      loadOrders();
    } catch (error: any) {
      message.error('Servis fişi silinirken hata oluştu');
    }
  };

  const closeCancelModal = () => {
    if (!cancelLoading) setCancelModalOrder(null);
  };

  /** Teslim edildi (5) ve İptal (6) fişler düzenlenemez */
  const canEditServiceOrder = (status: number) => status !== 5 && status !== 6;

  const executeServiceOrderCancel = async (copyToNewOrder: boolean) => {
    if (!cancelModalOrder) return;
    setCancelLoading(true);
    try {
      const res = await cancelServiceOrder(cancelModalOrder.id, copyToNewOrder);
      if (res.warning) message.warning(res.warning);
      else message.success(copyToNewOrder ? 'Fiş iptal edildi; kopya oluşturuldu.' : 'Fiş iptal edildi.');
      setCancelModalOrder(null);
      await loadOrders();
      if (copyToNewOrder && res.newOrderId) navigate(`/service-orders/${res.newOrderId}`);
    } catch (error: any) {
      const msg = error.response?.data?.message ?? error.message;
      message.error(typeof msg === 'string' ? msg : 'İptal sırasında hata oluştu');
    } finally {
      setCancelLoading(false);
    }
  };

  const lineDescription = (line: ServiceOrderLineDto) => {
    if (line.itemType === 1) return line.serviceItemName || '—';
    if (line.itemType === 2) return [line.productCode, line.productName].filter(Boolean).join(' — ') || '—';
    if (line.itemType === 3) return line.externalServiceItemName || '—';
    return '—';
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return 'blue';
      case 2:
        return 'orange';
      case 3:
        return 'purple';
      case 4:
        return 'green';
      case 5:
        return 'cyan';
      case 6:
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusRowStyle = (status: number): React.CSSProperties => {
    const colors: Record<number, { bg: string; border?: string }> = {
      1: { bg: 'rgba(22, 119, 255, 0.08)', border: '1px solid rgba(22, 119, 255, 0.3)' },
      2: { bg: 'rgba(250, 140, 22, 0.08)', border: '1px solid rgba(250, 140, 22, 0.3)' },
      3: { bg: 'rgba(114, 46, 209, 0.08)', border: '1px solid rgba(114, 46, 209, 0.3)' },
      4: { bg: 'rgba(82, 196, 26, 0.08)', border: '1px solid rgba(82, 196, 26, 0.3)' },
      5: { bg: 'rgba(13, 202, 240, 0.08)', border: '1px solid rgba(13, 202, 240, 0.3)' },
      6: { bg: 'rgba(255, 77, 79, 0.06)', border: '1px solid rgba(255, 77, 79, 0.25)' },
    };
    return colors[status] ? { backgroundColor: colors[status].bg, borderLeft: colors[status].border } : {};
  };

  const loadFicheDocumentContext = async (orderId: number) => {
    const fullOrder = await fetchServiceOrder(orderId);
    const [accountRes, vehicleRes] = await Promise.allSettled([
      api.get(`/accounts/${fullOrder.customerId}`),
      fetchCustomerVehicle(fullOrder.vehicleId),
    ]);
    const accountData = accountRes.status === 'fulfilled' ? accountRes.value.data : null;
    const vehicleData = vehicleRes.status === 'fulfilled' ? vehicleRes.value : null;
    return { fullOrder, accountData, vehicleData };
  };

  const openFicheContentModal = async (order: ServiceOrderDto) => {
    setFichePreviewOrderId(order.id);
    setFichePreviewTitle(order.orderNo ? `Servis fişi — ${order.orderNo}` : 'Servis fişi içeriği');
    setFichePreviewOpen(true);
    setFichePreviewLoading(true);
    setFichePreviewHtml(null);
    try {
      const { fullOrder, accountData } = await loadFicheDocumentContext(order.id);
      fichePreviewSnapshotRef.current = { fullOrder, accountData };
      setFichePreviewHtml(
        buildServiceOrderFicheModalPreviewHtml({
          fullOrder,
          accountData,
          theme,
        })
      );
    } catch (err: any) {
      message.error(err.response?.data?.message ?? 'Fiş içeriği yüklenemedi');
      setFichePreviewOpen(false);
      setFichePreviewOrderId(null);
      fichePreviewSnapshotRef.current = null;
    } finally {
      setFichePreviewLoading(false);
    }
  };

  const closeFichePreview = () => {
    setFichePreviewOpen(false);
    setFichePreviewHtml(null);
    setFichePreviewOrderId(null);
    fichePreviewSnapshotRef.current = null;
  };

  /** Liste / modal Yazdır: fiş formundaki yazdır ile aynı (yeni sekme, logo kırılımları, otomatik yazdır). */
  const printServiceOrderById = async (orderId: number) => {
    const { fullOrder, accountData, vehicleData } = await loadFicheDocumentContext(orderId);
    const origin = window.location.origin;
    let printContent = buildServiceOrderFicheDocumentHtml({
      fullOrder,
      accountData,
      vehicleData,
      origin,
      forPrint: true,
    });
    printContent = insertLogoBeforeAdditionalTables(printContent, origin);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(printContent);
      win.document.close();
    } else {
      message.warning('Yazdırma penceresi açılamadı; tarayıcı pop-up engelini kontrol edin.');
    }
  };

  const handlePrintFromList = async (order: ServiceOrderDto) => {
    try {
      await printServiceOrderById(order.id);
    } catch (err: any) {
      message.error(err.response?.data?.message ?? 'Yazdırma sırasında hata oluştu');
    }
  };

  const getActionMenuItems = (record: ServiceOrderDto): MenuProps['items'] => [
    ...(canEditServiceOrder(record.status)
      ? [{ key: 'edit', icon: <EditOutlined />, label: 'Düzenle', onClick: () => handleEdit(record) }]
      : []),
    { key: 'print', icon: <PrinterOutlined />, label: 'Yazdır', onClick: () => handlePrintFromList(record) },
    ...(canCancelServiceOrder && record.status === 5
      ? [
          {
            key: 'cancelFiche',
            label: 'İptal',
            danger: true,
            onClick: () => setCancelModalOrder(record),
          },
        ]
      : []),
    ...(canDelete
      ? [{
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Sil',
          danger: true,
          disabled: !canEditServiceOrder(record.status),
          onClick: () => {
            Modal.confirm({
              title: 'Bu servis fişini silmek istediğinizden emin misiniz?',
              okText: 'Evet',
              cancelText: 'Hayır',
              onOk: () => handleDelete(record.id),
            });
          },
        }]
      : []),
  ];

  const columns = [
    {
      title: 'Müşteri',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 240,
      ellipsis: true,
      render: (text: string, record: ServiceOrderDto) => (
        <Typography.Link
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void openFicheContentModal(record);
          }}
          style={{ display: 'block', maxWidth: '100%' }}
          ellipsis
        >
          {text}
        </Typography.Link>
      ),
    },
    { title: 'Araç', dataIndex: 'vehiclePlate', key: 'vehiclePlate', width: 160, ellipsis: true },
    {
      title: 'Fiş Tarihi',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 140,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: number, record: ServiceOrderDto) => (
        <Tag color={getStatusColor(status)}>{record.statusText}</Tag>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: isMobile ? 80 : 200,
      align: 'right' as const,
      fixed: 'right' as const,
      render: (_: any, record: ServiceOrderDto) =>
        isMobile ? (
          <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={['click']}>
            <Button type="text" size="small" icon={<MoreOutlined />} />
          </Dropdown>
        ) : (
          <Space>
            {canEditServiceOrder(record.status) && (
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              Düzenle
            </Button>
            )}
            <Button type="link" size="small" icon={<PrinterOutlined />} onClick={() => handlePrintFromList(record)}>
              Yazdır
            </Button>
            {canCancelServiceOrder && record.status === 5 && (
              <Button type="link" danger size="small" onClick={() => setCancelModalOrder(record)}>
                İptal
              </Button>
            )}
            {canDelete &&
              (!canEditServiceOrder(record.status) ? (
                <Button type="link" danger size="small" icon={<DeleteOutlined />} disabled>
                  Sil
                </Button>
              ) : (
                <Popconfirm
                  title="Bu servis fişini silmek istediğinizden emin misiniz?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Evet"
                  cancelText="Hayır"
                >
                  <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                    Sil
                  </Button>
                </Popconfirm>
              ))}
          </Space>
        ),
    },
  ];

  return (
    <div className="service-orders-list-page">
      <div
        className="service-orders-list-header"
        style={{
          marginBottom: 16,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h1 style={{ margin: 0 }}>
          <FileTextOutlined /> Servis Fişleri
        </h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Servis Fişi
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} sm={12} md={5}>
            <Input
              placeholder="Müşteri adı soyadı"
              value={filterCustomerName}
              onChange={(e) => setFilterCustomerName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Plaka"
              value={filterPlate}
              onChange={(e) => setFilterPlate(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <DatePicker.RangePicker
              placeholder={['Başlangıç', 'Bitiş']}
              value={filterDateRange ?? [null, null]}
              onChange={(dates) => setFilterDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
            />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Select<number>
              placeholder="Tüm durumlar"
              value={filterStatus === null ? undefined : filterStatus}
              onChange={(v) => setFilterStatus(v === undefined ? null : v)}
              style={{ width: '100%' }}
              allowClear
              options={statuses.map((s) => ({ value: s.value, label: s.displayName }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Space>
              <Button type="primary" onClick={() => loadOrders()}>
                Filtrele
              </Button>
              <Button onClick={handleClearFilters}>
                Temizle
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          rowClassName={() => 'service-order-row'}
          onRow={(record) => ({ style: getStatusRowStyle(record.status) })}
          scroll={{ x: TABLE_SCROLL_X }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} servis fişi`,
          }}
        />
      </Card>

      <Modal
        title="Servis fişini iptal et"
        open={!!cancelModalOrder}
        onCancel={closeCancelModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        {cancelModalOrder && (
          <div>
            <p>
              <strong>Müşteri:</strong> {cancelModalOrder.customerName}
            </p>
            <p>
              <strong>Araç:</strong>{' '}
              {[cancelModalOrder.vehiclePlate, cancelModalOrder.vehicleInfo].filter(Boolean).join(' — ') || '—'}
            </p>
            <Table<ServiceOrderLineDto>
              size="small"
              pagination={false}
              rowKey={(r) => String(r.id)}
              dataSource={cancelModalOrder.lines ?? []}
              columns={[
                { title: 'Satır', dataIndex: 'lineNo', key: 'lineNo', width: 56 },
                { title: 'Tür', dataIndex: 'itemTypeText', key: 'itemTypeText', width: 100, ellipsis: true },
                {
                  title: 'Açıklama',
                  key: 'desc',
                  ellipsis: true,
                  render: (_: unknown, row) => lineDescription(row),
                },
                {
                  title: 'Miktar',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  width: 88,
                  align: 'right' as const,
                },
                {
                  title: 'Birim fiyat',
                  dataIndex: 'unitPrice',
                  key: 'unitPrice',
                  width: 100,
                  align: 'right' as const,
                  render: (v: number) => `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
                },
                {
                  title: 'KDV %',
                  dataIndex: 'taxRate',
                  key: 'taxRate',
                  width: 72,
                  align: 'center' as const,
                },
                {
                  title: 'Tutar (hariç)',
                  dataIndex: 'totalPrice',
                  key: 'totalPrice',
                  width: 110,
                  align: 'right' as const,
                  render: (v: number) => `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺`,
                },
              ]}
            />
            <Space wrap style={{ marginTop: 16 }} size="middle">
              <Button onClick={closeCancelModal} disabled={cancelLoading}>
                Vazgeç
              </Button>
              <Button danger loading={cancelLoading} onClick={() => executeServiceOrderCancel(false)}>
                Fişi İptal Et
              </Button>
              <Button type="primary" danger loading={cancelLoading} onClick={() => executeServiceOrderCancel(true)}>
                Fişi İptal Et ve Kopyala
              </Button>
            </Space>
          </div>
        )}
      </Modal>

      <Modal
        title={fichePreviewTitle}
        open={fichePreviewOpen}
        onCancel={closeFichePreview}
        footer={
          !fichePreviewLoading && fichePreviewHtml && fichePreviewOrderId != null ? (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                loading={fichePrintLoading}
                onClick={async () => {
                  setFichePrintLoading(true);
                  try {
                    await printServiceOrderById(fichePreviewOrderId);
                  } catch (err: any) {
                    message.error(err.response?.data?.message ?? 'Yazdırma sırasında hata oluştu');
                  } finally {
                    setFichePrintLoading(false);
                  }
                }}
              >
                Yazdır
              </Button>
            </div>
          ) : null
        }
        width="min(960px, 96vw)"
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        {fichePreviewLoading ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <Spin size="large" />
          </div>
        ) : fichePreviewHtml ? (
          <iframe
            title="Servis fişi önizleme"
            srcDoc={fichePreviewHtml}
            sandbox="allow-same-origin"
            style={{ width: '100%', height: 'min(72vh, 820px)', border: 'none', display: 'block' }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default ServiceOrdersList;
